import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  FormField,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Plus, Minus } from 'lucide-react';
import React from 'react';
import { FieldPathByValue, FieldValues } from 'react-hook-form';

import { FieldProps } from './shared';

export function NumberField<
  T extends FieldValues,
  P extends FieldPathByValue<T, number>,
>({
  control,
  name,
  rules,
  label,
  description,
  className,
  min = 1,
  max = 1000,
  ...props
}: FieldProps<T, P> & React.ComponentPropsWithoutRef<typeof Input>) {
  const handleChange = (num: number, onChange: (value: number) => void) => {
    if (num >= Number(min) && num <= Number(max)) {
      onChange(num);
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, ...field } }) => (
        <FormItem className="grow">
          <FormLabel>{label}</FormLabel>
          <div className="flex flex-row items-stretch justify-center">
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleChange(Number(field.value) - 1, onChange)}
              disabled={Number(field.value) <= Number(min)}
            >
              <Minus size={16} />
            </Button>
            <FormControl>
              <Input
                onChange={(e) => handleChange(Number(e.target.value), onChange)}
                min={min}
                max={max}
                type="text"
                inputMode="numeric"
                className={cn('text-center z-10', className)}
                {...props}
                {...field}
              />
            </FormControl>
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleChange(Number(field.value) + 1, onChange)}
              disabled={Number(field.value) >= Number(max)}
            >
              <Plus size={16} />
            </Button>
          </div>
          <FormDescription>{description?.(field.value)}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
